import { useEffect, useState } from 'react';

import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Divider from '@mui/joy/Divider';
import Tooltip from '@mui/joy/Tooltip';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionGroup from '@mui/joy/AccordionGroup';


import { FHRequestType } from 'stores/fhrequest_store';
import { FHRequestAttachmentType } from 'stores/fhrequest_attachment_store';
import useFHRequestAttachment from 'hooks/useFHRequestAttachment';
import Error from 'components/Error';
import { TimeUI } from 'components/Date';
import getIcon from 'utils/getIcon';
import pluralize from 'utils/pluralize';



const Attachment = ({ attachment }: { attachment: FHRequestAttachmentType }) => {
    const { fetchSignedUrl } = useFHRequestAttachment();
    const AttachmentIcon = getIcon('Attachment');
    const DownloadIcon = getIcon('Download');

    function redirect(disposition: 'inline' | 'attachment') {
        // Open the window before the async call. Browsers don't like
        // opening windows in async functions.
        const win = window.open(undefined, "_blank");

        fetchSignedUrl(attachment.key, disposition)
            .then((url: string | null) => {
                if (!url) {
                    Error("Unabled to load signed url for attachment");
                    return;
                }
                if (win) {
                    win.location.href = url;
                } else {
                    window.location.href = url;
                }
            });
    }

    return (
        <Stack spacing={1}>
            <Stack direction="row"
                spacing={2}
                alignItems="flex-start">

                <AttachmentIcon />


                <Stack spacing={0.5}>

                    <Tooltip arrow title={"Click to open in your browser"}>
                        <Typography
                            overflow="hidden"
                            level="body-sm"
                            color="primary"
                            sx={{ cursor: "pointer" }}
                            onClick={() => redirect('inline')}

                        >
                            {attachment.filename}
                        </Typography>
                    </Tooltip>

                    <Stack direction="row"
                        alignItems="center"
                        divider={<Divider orientation="vertical" />}
                        spacing={1}
                    >
                    <Tooltip arrow title={"Click to download this file"}>
                    <Typography
                        startDecorator={<DownloadIcon />}
                        sx={{cursor: "pointer"}}
                            onClick={() => redirect('attachment')}
                        level="body-xs"
                    >Download</Typography>
                    </Tooltip>

                    <Typography
                        level="body-xs"
                    >Added: <TimeUI ts={attachment.date_created} />
                    </Typography>
                    </Stack>
                </Stack>
            </Stack>


            </Stack>
    );
};

const RequestSidebarRequestAttachments = ({ request }: { request: FHRequestType }) => {

    const { fetchListByRequest, getListByRequest } = useFHRequestAttachment();
    const [loaded, setLoaded] = useState(false);
    const [attachments, setAttachments] = useState([] as FHRequestAttachmentType[]);
    const AttachIcon = getIcon('AttachFile');

    useEffect(() => {
        if(loaded) return;

        fetchListByRequest(request.key)
            .then(() => {
                setLoaded(true);
                setAttachments(getListByRequest(request.key));
            });

    }, [request, fetchListByRequest]);

    if(!attachments.length) return null;

    return (
        <AccordionGroup size="lg">
            <Accordion  >
                <AccordionSummary>
                    <AttachIcon />
                    <Typography level="body-sm">
                        { pluralize(attachments?.length, 'Attachment') }
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack
                        spacing={1}
                        divider={<Divider />}
                    >
            {
                attachments.map((attachment, index) =>
                    <Attachment attachment={attachment} key={index} />
                )
            }
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </AccordionGroup>

    );
};

export default RequestSidebarRequestAttachments;
